import { Grid, Table, type TableColumnsType, type TableProps } from "antd";
import clsx from "clsx";
import { useMemo } from "react";
import {
  applyFixedActionColumn,
  applyFixedActionToChildren,
  resolveScrollForFixed,
} from "./appTable.utils";

const { useBreakpoint } = Grid;

export type AppTableProps<T extends object> = TableProps<T> & {
  compact?: boolean;
  sortMode?: "local" | "api";
  /** Auto-fix cột Thao tác bên phải khi màn hình < lg (1024px). Mặc định: true */
  fixActionColumn?: boolean;
  onApiSortChange?: (sort: {
    field?: string;
    order?: "ascend" | "descend";
    sortType?: "ASC" | "DESC";
  }) => void;
};

const getValueByDataIndex = <T extends object>(
  record: T,
  dataIndex: unknown,
): unknown => {
  if (!dataIndex) return undefined;

  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>((value, key) => {
      if (value && typeof value === "object") {
        return (value as Record<string, unknown>)[String(key)];
      }
      return undefined;
    }, record);
  }

  const path = String(dataIndex).split(".");
  return path.reduce<unknown>((value, key) => {
    if (value && typeof value === "object") {
      return (value as Record<string, unknown>)[key];
    }
    return undefined;
  }, record);
};

const compareValue = (left: unknown, right: unknown) => {
  if (left == null && right == null) return 0;
  if (left == null) return -1;
  if (right == null) return 1;

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), "vi", {
    numeric: true,
    sensitivity: "base",
  });
};

const getSortField = (field: unknown): string | undefined => {
  if (!field) return undefined;
  if (Array.isArray(field)) return field.join(".");
  return String(field);
};

export const AppTable = <T extends object>({
  className,
  compact,
  columns,
  children,
  scroll,
  fixActionColumn = true,
  onApiSortChange,
  onChange,
  pagination,
  sortMode = "local",
  ...props
}: AppTableProps<T>) => {
  const screens = useBreakpoint();
  const shouldFixAction = fixActionColumn && screens.lg !== true;

  const enhancedColumns = useMemo(() => {
    const withSorter = (columns?: TableColumnsType<T>) => {
      if (!columns) return columns;

      return columns.map((column) => {
        const dataIndex = "dataIndex" in column ? column.dataIndex : undefined;

        if (column.sorter !== undefined || !dataIndex) {
          return column;
        }

        return {
          ...column,
          sorter:
            sortMode === "api"
              ? true
              : (left: T, right: T) =>
                  compareValue(
                    getValueByDataIndex(left, dataIndex),
                    getValueByDataIndex(right, dataIndex),
                  ),
        };
      }) as TableColumnsType<T>;
    };

    return applyFixedActionColumn(withSorter(columns), shouldFixAction);
  }, [columns, sortMode, shouldFixAction]);

  const enhancedChildren = useMemo(
    () => applyFixedActionToChildren(children, shouldFixAction),
    [children, shouldFixAction],
  );

  const tableScroll = useMemo(
    () => resolveScrollForFixed(scroll, shouldFixAction),
    [scroll, shouldFixAction],
  );

  return (
    <Table<T>
      className={clsx(
        "app-ant-table",
        compact && "app-ant-table-compact",
        shouldFixAction && "app-ant-table--fixed-action",
        className,
      )}
      columns={enhancedColumns}
      scroll={tableScroll}
      onChange={(paginationConfig, filters, sorter, extra) => {
        if (sortMode === "api") {
          const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
          const order = activeSorter?.order || undefined;

          onApiSortChange?.({
            field: getSortField(activeSorter?.field || activeSorter?.columnKey),
            order,
            sortType:
              order === "ascend"
                ? "ASC"
                : order === "descend"
                  ? "DESC"
                  : undefined,
          });
        }

        onChange?.(paginationConfig, filters, sorter, extra);
      }}
      pagination={
        pagination === false
          ? false
          : {
              showSizeChanger: false,
              ...pagination,
            }
      }
      {...props}
    >
      {enhancedChildren}
    </Table>
  );
};
